import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { FILTER_LABELS, PRESETS, addDays } from '@/analytics/lib';
import { CloseButton, useDialogs } from './dialogs';
import { Icon } from './icons';
import { Badge, FormField, Notice, SelectWrap } from './ui';
import { deleteReport, modelLabel, presetLabel, rangeText, saveReport } from './data';

// The report screens' dialogs: the reporting period, filters, table columns, saving and deleting reports.

const iso = d => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null);
const fromIso = s => (s ? new Date(`${s}T12:00:00`) : undefined);

// ── Reporting period ──
export function DateDialog({ state, update }) {
  const { closeDialog } = useDialogs();
  const [draft, setDraft] = useState({ from: fromIso(state.since), to: fromIso(state.until) });
  const pick = id => {
    update(id === 'custom' ? { range: 'custom', since: state.since, until: state.until } : { range: id === '30d' ? null : id, since: null, until: null });
    closeDialog();
    toast(`${presetLabel(id)} applied`);
  };
  const applyCustom = () => {
    if (!draft?.from) return;
    const since = iso(draft.from), until = iso(draft.to || draft.from);
    update({ range: 'custom', since, until: until < since ? since : until });
    closeDialog();
    toast('Custom range applied');
  };
  return <>
    <p>Select a reporting period. Every number on this screen follows it, including the comparison with the period before.</p>
    <div className="date-presets">
      {PRESETS.filter(p => p.id !== 'custom').map(p => <button key={p.id} type="button" className={state.preset === p.id ? 'selected' : ''} onClick={() => pick(p.id)}>{p.label}</button>)}
    </div>
    <div className="between"><span className="label">Selected period</span><span>{rangeText(state)}</span></div>
    <div className="calendar-head"><strong>Custom range</strong><span className="muted">{draft?.from ? `${iso(draft.from)} → ${iso(draft.to || draft.from)}` : 'Pick a start and end day'}</span></div>
    <Calendar mode="range" selected={draft} numberOfMonths={1} onSelect={setDraft} disabled={{ after: fromIso(state.today) }} defaultMonth={fromIso(state.until)} />
    <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
      <button type="button" className="btn" disabled={!draft?.from} onClick={applyCustom}><Icon name="calendar" />Apply custom range</button>
    </div>
    <Notice icon="clock">{state.compare ? `Comparison uses the preceding period of equal length (${addDays(state.since, -state.days)} – ${addDays(state.since, -1)}).` : 'Period comparison is off.'}</Notice>
  </>;
}
export const DateFooter = () => <CloseButton primary>Done</CloseButton>;

// ── Filters: narrow the screen by source, medium or campaign ──
const FILTERABLE = ['source', 'medium', 'campaign'];
export function FilterDialog({ state, update }) {
  const { closeDialog } = useDialogs();
  const dims = state.dims || {};
  const [draft, setDraft] = useState(Object.fromEntries(FILTERABLE.map(k => [k, state.filters[k] || ''])));
  const apply = () => { update(Object.fromEntries(FILTERABLE.map(k => [k, draft[k] || null]))); closeDialog(); };
  const reset = () => setDraft(Object.fromEntries(FILTERABLE.map(k => [k, ''])));
  return <>
    <p>Narrow the workspace to the traffic you want to evaluate. Filters apply to every number on this screen.</p>
    {FILTERABLE.map(key => (
      <FormField key={key} label={FILTER_LABELS[key]}>
        <select value={draft[key]} onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} aria-label={`${FILTER_LABELS[key]} filter`}>
          <option value="">All {FILTER_LABELS[key].toLowerCase()}s</option>
          {(dims[key] || []).map(o => <option key={String(o.value)} value={o.value}>{o.value} ({o.count})</option>)}
          {draft[key] && !(dims[key] || []).some(o => String(o.value) === draft[key]) && <option value={draft[key]}>{draft[key]}</option>}
        </select>
      </FormField>
    ))}
    <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
      <button type="button" className="btn" onClick={reset}>Reset</button>
      <button type="button" className="btn primary" onClick={apply}><Icon name="check" />Apply filters</button>
    </div>
  </>;
}

// ── Table columns ──
export function ColumnsDialog({ all, selected, onApply }) {
  const { closeDialog } = useDialogs();
  const [draft, setDraft] = useState(new Set(selected));
  const toggle = k => setDraft(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const apply = () => { if (!draft.size) { toast('Select at least one metric'); return; } onApply(all.filter(c => draft.has(c.key)).map(c => c.key)); closeDialog(); };
  return <>
    <p>Select the metrics you want to see in the campaign table.</p>
    <div className="checkbox-grid">{all.map(c => <label key={c.key}><input type="checkbox" name="column" value={c.key} checked={draft.has(c.key)} onChange={() => toggle(c.key)} />{c.label}</label>)}</div>
    <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
      <CloseButton>Cancel</CloseButton>
      <button type="button" className="btn primary" onClick={apply}><Icon name="check" />Apply columns</button>
    </div>
  </>;
}

// ── Save the current perspective as a report ──
export function SaveReportDialog({ state, page, defaultName = '' }) {
  const { closeDialog } = useDialogs();
  const qc = useQueryClient();
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async e => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true); setError('');
    try {
      const params = Object.fromEntries([...state.params.entries()].filter(([k]) => !['contact', 'contactTab'].includes(k)));
      await saveReport(name.trim(), { ...params, page, description: description.trim() });
      await qc.invalidateQueries({ queryKey: ['analytics-views'] });
      closeDialog();
      toast('Report saved. Find it in Saved reports.');
    } catch (err) {
      setError(err.message || 'Could not save the report.');
    } finally { setBusy(false); }
  };
  return <>
    <p>Keep this combination of filters and attribution settings so you can return to it.</p>
    <form id="report-form" onSubmit={submit} aria-busy={busy}>
      <FormField label="Report name"><input id="report-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Weekly acquisition review" required maxLength={70} autoFocus /></FormField>
      <FormField label="Description" optional><textarea id="report-description" rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this report help you understand?" maxLength={200} /></FormField>
    </form>
    {error && <div className="inline-error" role="alert">{error}</div>}
    <div className="row" style={{ flexWrap: 'wrap', marginTop: 16 }}>
      <Badge>{modelLabel(state.model)}</Badge>{state.filters.source && <Badge>{state.filters.source}</Badge>}<Badge>{presetLabel(state.preset)}</Badge>
    </div>
    <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
      <CloseButton>Cancel</CloseButton>
      <button type="submit" form="report-form" className="btn primary" disabled={!name.trim() || busy} aria-busy={busy || undefined}><Icon name="save" />{busy ? 'Saving…' : 'Save report'}</button>
    </div>
  </>;
}

// ── Delete a saved report ──
export function DeleteReportDialog({ report }) {
  const { closeDialog } = useDialogs();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const remove = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await deleteReport(report.id);
      await qc.invalidateQueries({ queryKey: ['analytics-views'] });
      closeDialog();
      toast('Report deleted');
    } catch (err) { setError(err.message || 'Could not delete the report.'); } finally { setBusy(false); }
  };
  return <>
    <p>Remove “{report.name}” from this workspace? Your campaign data is unaffected.</p>
    {error && <div className="inline-error" role="alert">{error}</div>}
    <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
      <CloseButton>Keep report</CloseButton>
      <button type="button" className="btn primary" onClick={remove} disabled={busy} aria-busy={busy || undefined}>{busy ? 'Deleting…' : 'Delete report'}</button>
    </div>
  </>;
}

// A select whose label is a prefix ("Attribution: Linear"), as in the prototype toolbar.
export const ModelSelect = ({ state, update, prefix = 'Attribution:', options }) => (
  <SelectWrap label="Attribution model" prefix={prefix} icon="target" value={state.model} options={options} onChange={v => update({ model: v === 'last' ? null : v })} />
);
